import { Query, useQuery } from "@tanstack/react-query";
import { fetchOrder, fetchOrderHead, OrderHead } from "src/api/order.api";
import { GetOrderDto } from "src/services/generated-api";

export const orderQueryKey = (orderId: string) => ["order", orderId] as const;

export function useOrderQuery(
  orderId: string | null,
  guestToken: string | null,
  options: {
    refetchInterval?: number | false | ((query: Query<GetOrderDto>) => number | false);
  } = {},
) {
  return useQuery<GetOrderDto>({
    queryKey: orderQueryKey(orderId ?? ""),
    queryFn: () => {
      if (!orderId || !guestToken) {
        throw new Error("orderId and guestToken are required");
      }
      return fetchOrder(orderId, guestToken);
    },
    enabled: Boolean(orderId && guestToken),
    staleTime: 10 * 1000,
    // The order cache is also written by mutation responses (setQueryData in the
    // order/delivery/additional-services flows) that come from endpoints WITHOUT
    // the PhotoSignInterceptor, so their photos have empty `url`s. Always refetch
    // on mount so each step (e.g. photo-options) gets a freshly signed order and
    // photo thumbnails render without needing a manual page refresh.
    refetchOnMount: "always",
    refetchInterval: options.refetchInterval ?? false,
  });
}

/**
 * `fetchOrderHead` under its OWN key: a partial order must never land in the
 * ["order", id] cache the checkout reads its full order from.
 */
export const orderHeadQueryKey = (orderId: string) => ["order-head", orderId] as const;

export function useOrderHeadQuery(
  orderId: string | null,
  guestToken: string | null,
  options: {
    refetchInterval?: number | false | ((query: Query<OrderHead>) => number | false);
  } = {},
) {
  return useQuery<OrderHead>({
    queryKey: orderHeadQueryKey(orderId ?? ""),
    queryFn: () => {
      if (!orderId || !guestToken) {
        throw new Error("orderId and guestToken are required");
      }
      return fetchOrderHead(orderId, guestToken);
    },
    enabled: Boolean(orderId && guestToken),
    staleTime: 10 * 1000,
    refetchInterval: options.refetchInterval ?? false,
  });
}
